/**
 * SatellitePresence · "See the Federation breathe" widget.
 *
 * Polls /api/satellite/presence every 30s and renders the 7 known chapels
 * (geminus / aria / roger5 / keys / axiom / oracle / scholar) with a status
 * dot + last-seen timestamp + ping count in the last 24h. Plus any extra
 * non-canonical hosts that have pinged in.
 *
 * Status tiers:
 *   LIVE (green · pulse)  · last ping ≤ 10 min ago
 *   WARM (gold)           · ≤ 60 min
 *   IDLE (violet)         · ≤ 24 hours
 *   DARK (red)            · no ping in window
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

const TIER = {
  LIVE: { color: "green",   pulse: true,  label: "LIVE" },
  WARM: { color: "gold",    pulse: false, label: "WARM" },
  IDLE: { color: "violet",  pulse: false, label: "IDLE" },
  DARK: { color: "red",     pulse: false, label: "DARK" },
};

const CHAPEL_LABEL = {
  geminus: "Mirror",
  aria:    "Aria",
  roger5:  "Roger AI",
  keys:    "KEYS",
  axiom:   "Axiom",
  oracle:  "Oracle",
  scholar: "Scholar",
};

function ago(iso) {
  if (!iso) return "never";
  try {
    const t = new Date(iso).getTime();
    const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  } catch { return "—"; }
}

export default function SatellitePresence() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const d = await api.satellitePresence(24);
        if (alive) { setData(d); setErr(null); }
      } catch (e) {
        if (alive) setErr(e.message || "offline");
      }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <div data-testid="satellite-presence" className="mb-8 border border-[var(--chrome)] bg-[var(--void-2)] p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-glow-magenta">
            ◈ Federation Breath · Satellite Presence
          </div>
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--text-3)] mt-1">
            heartbeat ledger · last 24h window
          </div>
        </div>
        {data && (
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] flex items-center gap-3">
            <span className="text-glow-green">{data.live_count}/{data.chapels.length} LIVE</span>
            <span className="text-[var(--text-3)]">{data.total_pings_in_window} pings</span>
          </div>
        )}
      </div>
      {err && (
        <div className="font-mono text-[10px] text-glow-red mb-3">presence stream offline · {err}</div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {(data?.chapels || []).map((c) => {
          const tier = TIER[c.status] || TIER.DARK;
          return (
            <div
              key={c.host}
              data-testid={`presence-chapel-${c.host}`}
              className="border border-[var(--chrome)] bg-[rgba(7,8,26,0.6)] p-3 flex flex-col gap-1"
              style={{ borderColor: c.status === "LIVE" ? `var(--${tier.color})` : undefined }}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`inline-block w-2 h-2 rounded-full ${tier.pulse ? "animate-pulse" : ""}`}
                  style={{ background: `var(--${tier.color})`, boxShadow: `0 0 6px var(--${tier.color})` }}
                />
                <span
                  className="font-mono text-[10px] uppercase tracking-[0.22em]"
                  style={{ color: `var(--${tier.color})` }}
                >
                  {tier.label}
                </span>
              </div>
              <div className="font-display text-sm text-[var(--text)] truncate">
                {CHAPEL_LABEL[c.host] || c.host}
              </div>
              <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--text-3)]">
                {ago(c.last_seen)}
              </div>
              <div className="font-mono text-[9px] text-[var(--text-4)]">
                {c.count_in_window} ping{c.count_in_window === 1 ? "" : "s"}
              </div>
            </div>
          );
        })}
      </div>
      {data?.extras?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[var(--chrome)]">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--text-3)] mb-2">
            ◇ non-canonical chapels (custom embeds)
          </div>
          <div className="flex flex-wrap gap-2">
            {data.extras.map((c) => (
              <span
                key={c.host}
                className="font-mono text-[9px] uppercase tracking-[0.18em] px-2 py-1 border border-[var(--chrome)] text-[var(--text-2)]"
              >
                {c.host} · {ago(c.last_seen)} · {c.count_in_window}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
